import type {
  ClipFraming,
  FilterState,
  TimelineAudioTrack,
  TimelineClip,
  TimelineProject,
} from "@scroll/shared-types";
import type { TimelineCapabilities } from "@/lib/timeline-capabilities";

export interface PersistedFilterMetadata {
  filterId: string | null;
  intensity: number;
}

export interface PersistedGreenScreenSettings {
  enabled: boolean;
  mode: "chroma";
  keyColor: string;
  similarity: number;
  blend: number;
  spill?: number;
  foregroundFit?: "contain" | "cover";
  foregroundPositionX?: number;
  foregroundPositionY?: number;
  foregroundScale?: number;
  background:
    | { type: "color"; color: string }
    | {
        type: "image";
        source: { kind: "blob"; url: string; blobKey: string };
        fit: "cover";
      };
}

export interface PersistedClipForWire {
  id: string;
  type: "video" | "image";
  source?: { kind: "mux"; assetId: string } | { kind: "blob"; url: string; blobKey: string };
  sourceDuration: number;
  sourceWidth: number;
  sourceHeight: number;
  timelineStart?: number;
  trimStart: number;
  trimEnd: number;
  displayDuration: number;
  speed: number;
  volume: number;
  filter: PersistedFilterMetadata | null;
  framing?: ClipFraming;
  greenScreen?: PersistedGreenScreenSettings | null;
}

export interface PersistedTransitionForWire {
  afterClipIndex: number;
  type: string;
  duration: number;
}

export interface PersistedIntroTransitionForWire {
  type: "fade-in" | "fade-in-white";
  duration: number;
}

export interface PersistedOutroTransitionForWire {
  type: "fade-out" | "fade-out-white";
  duration: number;
}

export interface PersistedAudioTrackForWire {
  id: string;
  type: "music" | "voiceover" | "sfx";
  source: { kind: "blob"; url: string; blobKey: string } | { kind: "musicCatalog"; trackId: number };
  sourceDuration: number;
  timelineStart: number;
  trimStart: number;
  trimEnd: number;
  volumeKeyframes: Array<{ time: number; value: number }>;
  musicTrackId?: number;
  title?: string;
  artist?: string;
}

export interface PersistedTimelineProjectForWire {
  version: 1;
  aspectRatio: "9:16" | "16:9" | "1:1" | "4:5";
  outputWidth: number;
  outputHeight: number;
  clips: PersistedClipForWire[];
  transitions: PersistedTransitionForWire[];
  audioTracks: PersistedAudioTrackForWire[];
  introTransition?: PersistedIntroTransitionForWire | null;
  outroTransition?: PersistedOutroTransitionForWire | null;
}

function greenScreenToWire(clip: TimelineClip): PersistedGreenScreenSettings | null | undefined {
  const greenScreen = clip.greenScreen;
  if (greenScreen === undefined) return undefined;
  if (!greenScreen?.enabled) return null;

  const base = {
    enabled: true,
    mode: "chroma" as const,
    keyColor: greenScreen.keyColor,
    similarity: greenScreen.similarity,
    blend: greenScreen.blend,
    ...(greenScreen.spill != null ? { spill: greenScreen.spill } : {}),
    ...(greenScreen.foregroundFit ? { foregroundFit: greenScreen.foregroundFit } : {}),
    ...(greenScreen.foregroundPositionX != null ? { foregroundPositionX: greenScreen.foregroundPositionX } : {}),
    ...(greenScreen.foregroundPositionY != null ? { foregroundPositionY: greenScreen.foregroundPositionY } : {}),
    ...(greenScreen.foregroundScale != null ? { foregroundScale: greenScreen.foregroundScale } : {}),
  };

  if (greenScreen.background.type === "color") {
    return {
      ...base,
      background: {
        type: "color",
        color: greenScreen.background.color,
      },
    };
  }

  if (!greenScreen.background.blobKey || !greenScreen.background.blobUrl) {
    throw new Error("Green screen image background must finish uploading before publish");
  }

  return {
    ...base,
    background: {
      type: "image",
      source: {
        kind: "blob",
        url: greenScreen.background.blobUrl,
        blobKey: greenScreen.background.blobKey,
      },
      fit: "cover",
    },
  };
}

function clipToWire(clip: TimelineClip): PersistedClipForWire {
  const greenScreen = greenScreenToWire(clip);
  return {
    id: clip.id,
    type: clip.type,
    ...(clip.blobKey && clip.blobUrl
      ? { source: { kind: "blob" as const, url: clip.blobUrl, blobKey: clip.blobKey } }
      : {}),
    sourceDuration: clip.sourceDuration,
    sourceWidth: clip.sourceWidth,
    sourceHeight: clip.sourceHeight,
    ...(clip.timelineStart != null ? { timelineStart: clip.timelineStart } : {}),
    trimStart: clip.trimStart,
    trimEnd: clip.trimEnd,
    displayDuration: clip.displayDuration,
    speed: clip.speed,
    volume: clip.volume,
    filter: clip.filter
      ? { filterId: clip.filter.filterId, intensity: clip.filter.intensity }
      : null,
    ...(clip.framing ? { framing: clip.framing } : {}),
    ...(greenScreen !== undefined ? { greenScreen } : {}),
  };
}

function audioTrackToWire(track: TimelineAudioTrack): PersistedAudioTrackForWire {
  // Prefer device-uploaded blob sources over catalog ids when both are present —
  // explicit blobKey/blobUrl means the user picked their own file.
  const source: PersistedAudioTrackForWire["source"] | null =
    track.blobKey && track.blobUrl
      ? { kind: "blob", url: track.blobUrl, blobKey: track.blobKey }
      : track.type === "music" && typeof track.musicTrackId === "number"
        ? { kind: "musicCatalog", trackId: track.musicTrackId }
        : null;
  if (!source) {
    throw new Error("Timeline audio track must have a server-resolvable source before publish");
  }

  return {
    id: track.id,
    type: track.type,
    source,
    sourceDuration: track.sourceDuration,
    timelineStart: track.timelineStart,
    trimStart: track.trimStart,
    trimEnd: track.trimEnd,
    volumeKeyframes: track.volumeKeyframes,
    ...(track.musicTrackId != null ? { musicTrackId: track.musicTrackId } : {}),
    ...(track.title ? { title: track.title } : {}),
    ...(track.artist ? { artist: track.artist } : {}),
  };
}

export function toPersistedTimelineProject(
  project: TimelineProject,
): PersistedTimelineProjectForWire {
  return {
    version: 1,
    aspectRatio: project.aspectRatio,
    outputWidth: project.outputWidth,
    outputHeight: project.outputHeight,
    clips: project.clips.map(clipToWire),
    transitions: project.transitions.map((transition, index) => ({
      afterClipIndex: index,
      type: transition.type,
      duration: transition.duration,
    })),
    audioTracks: project.audioTracks.map(audioTrackToWire),
    ...(project.introTransition && (project.introTransition.type === "fade-in" || project.introTransition.type === "fade-in-white")
      ? {
          introTransition: {
            type: project.introTransition.type,
            duration: project.introTransition.duration,
          },
        }
      : {}),
    ...(project.outroTransition && (project.outroTransition.type === "fade-out" || project.outroTransition.type === "fade-out-white")
      ? {
          outroTransition: {
            type: project.outroTransition.type,
            duration: project.outroTransition.duration,
          },
        }
      : {}),
  };
}

function filtersEqual(a: FilterState | null, b: FilterState | null): boolean {
  if (!a && !b) return true;
  if (!a || !b) return false;
  return a.filterId === b.filterId && a.intensity === b.intensity;
}

function getCommonVideoFilter(project: TimelineProject): FilterState | null | undefined {
  const videoClips = project.clips.filter((clip) => clip.type === "video");
  if (videoClips.length === 0) return undefined;
  const [first] = videoClips.map((clip) => clip.filter);
  return videoClips.every((clip) => filtersEqual(first, clip.filter)) ? first : undefined;
}

export function serializeTimelineProject(
  project: TimelineProject,
  caps: TimelineCapabilities,
): TimelineProject {
  let clips = project.clips;

  if (!caps.supportsSplit && !caps.supportsMultiClip && clips.length > 1) {
    const commonVideoFilter = getCommonVideoFilter(project);
    const first = { ...clips[0] };
    if (first.type === "video" && commonVideoFilter !== undefined) {
      first.filter = commonVideoFilter;
    }
    clips = [first];
  }

  if (!caps.supportsMultiClip && clips.length > 1) {
    const primarySourceUri = clips[0].sourceUri;
    clips = clips.filter((clip, index) =>
      index === 0
      || clip.sourceUri === primarySourceUri
      || (!!clip.blobKey && !!clip.blobUrl),
    );
  }

  if (caps.maxClips > 0 && clips.length > caps.maxClips) {
    clips = clips.slice(0, caps.maxClips);
  }

  if (!caps.supportsClipFraming) {
    clips = clips.map((clip) => clip.framing ? { ...clip, framing: undefined } : clip);
  }

  let transitions = caps.supportsTransitions ? project.transitions : [];
  const maxTransitions = Math.max(0, clips.length - 1);
  if (transitions.length > maxTransitions) {
    transitions = transitions.slice(0, maxTransitions);
  }

  return { ...project, clips, transitions };
}

export function timelineHasNonIdentityEdits(project: TimelineProject): boolean {
  if (project.audioTracks.length > 0) return true;
  if (project.clips.length > 1) return true;
  if (project.transitions.some((transition) => transition.type !== "cut")) return true;
  if (project.introTransition && project.introTransition.type !== "cut") return true;
  if (project.outroTransition && project.outroTransition.type !== "cut") return true;
  return project.clips.some((clip) =>
    clip.trimStart > 0
    || clip.trimEnd < clip.sourceDuration
    || Math.abs(clip.speed - 1) > 1e-6
    || Math.abs(clip.volume - 1) > 1e-6
    || clip.framing?.fit === "cover"
    || (clip.framing?.scale != null && Math.abs(clip.framing.scale - 1) > 1e-6)
    || (clip.framing && (Math.abs(clip.framing.positionX - 0.5) > 1e-6 || Math.abs(clip.framing.positionY - 0.5) > 1e-6))
    || (clip.filter?.filterId && clip.filter.intensity > 0)
    || clip.greenScreen?.enabled
  );
}
